import { useCallback } from 'react';
import { availableAgents } from '@/utils/a2a/registry/agentRegistry';
import { useA2AMessageHandler } from './a2a/useA2AMessageHandler';
import { useA2APeerCommunication } from './a2a/useA2APeerCommunication';
import { useA2AOrchestration } from './a2a/useA2AOrchestration';
import { useA2AMessaging } from './a2a/useA2AMessaging';
import { useA2AMessageRouting } from './a2a/useA2AMessageRouting';
import { useA2AContextOperations } from './a2a/useA2AContextOperations';
import { useA2ACapabilityManagement } from './a2a/useA2ACapabilityManagement';
import { useCalendarOperations } from './calendar/useCalendarOperations';
import { useDataAgentOperations } from './data/useDataAgentOperations';
import { useSchedulingAgent } from './scheduler/useSchedulingAgent';

export const useA2AProtocol = () => {
  const {
    pendingMessages,
    agentResponses,
    sendMessage: sendA2AMessage,
    setPendingMessages,
    setAgentResponses,
    cleanupOldMessages,
    messageStats,
    broadcastNotification,
  } = useA2AMessageHandler();

  const {
    peerMessages,
    sendPeerMessage,
    findBestPeerAgent,
    setPeerMessages,
    cleanupOldPeerMessages,
    getPeerMessageStats
  } = useA2APeerCommunication();

  const {
    isProcessing,
    orchestrationPlans,
    setIsProcessing,
    setOrchestrationPlans,
    updateOrchestrationPlans,
    runMultiAgentTask: originalRunMultiAgentTask,
  } = useA2AOrchestration();

  const { createDirectChannel, getMessageBusStats, subscribeToMessages } = useA2AMessaging();
  
  const { routeMessage, isProcessing: isRouting } = useA2AMessageRouting();
  const contextOps = useA2AContextOperations();
  const { advertiseCapabilities, getAgentByCapability } = useA2ACapabilityManagement(setPendingMessages);
  
  const calendarOps = useCalendarOperations();
  const dataAgentOps = useDataAgentOperations();
  const schedulingAgent = useSchedulingAgent();

  // Enhanced sendMessage with intelligent routing
  const sendMessage = useCallback(async (message: {
    content: string;
    toAgent: string;
    metadata?: Record<string, any>;
  }) => {
    console.log(`🎯 A2A Protocol: Sending intelligent message to ${message.toAgent}`);
    
    // Enhanced metadata for intelligent orchestration
    const enhancedMetadata = {
      ...message.metadata,
      a2a_protocol_version: '2.0',
      intelligent_routing: true,
      context_propagation: true,
      timestamp: new Date().toISOString()
    };

    const enhancedMessage = {
      ...message,
      metadata: enhancedMetadata
    };

    const response = await routeMessage(enhancedMessage);
    setAgentResponses(prev => [...prev, response]);
    return response;
  }, [routeMessage, setAgentResponses]);

  // Enhanced multi-agent task with intelligent orchestration
  const runMultiAgentTask = useCallback((
    task: string, 
    capabilities: string[], 
    complexity: 'simple' | 'medium' | 'complex' = 'medium'
  ) => {
    console.log(`🧠 A2A Protocol: Running intelligent multi-agent task`);
    console.log(`📋 Task: "${task}"`);
    console.log(`⚙️ Capabilities: ${capabilities.join(', ')}`);
    console.log(`🎯 Complexity: ${complexity}`);

    return originalRunMultiAgentTask(task, capabilities, complexity, sendMessage);
  }, [originalRunMultiAgentTask, sendMessage]);

  // Enhanced agent discovery with capability matching
  const findOptimalAgents = useCallback((requiredCapabilities: string[]) => {
    console.log(`🔍 A2A Protocol: Finding optimal agents for capabilities: ${requiredCapabilities.join(', ')}`);
    
    // Filter available agents based on capabilities
    const optimalAgents = availableAgents.filter(agent => 
      agent.isAvailable && 
      requiredCapabilities.some(cap => agent.capabilities.includes(cap))
    );

    console.log(`✅ Found ${optimalAgents.length} optimal agents`);
    return optimalAgents;
  }, []);

  // Enhanced performance monitoring
  const getA2APerformanceMetrics = useCallback(() => {
    const totalMessages = pendingMessages.length + agentResponses.length;
    const successfulMessages = agentResponses.filter(r => !r.metadata?.error).length;
    const averageResponseTime = agentResponses.length > 0 ? 
      agentResponses.reduce((sum, r) => sum + (r.metadata?.executionTime || 1000), 0) / agentResponses.length : 0;

    return {
      totalMessages,
      successRate: totalMessages > 0 ? successfulMessages / totalMessages : 1,
      averageResponseTime,
      activeAgents: availableAgents.filter(a => a.isAvailable).length,
      orchestrationEfficiency: orchestrationPlans.length > 0 ? 0.85 : 0, // Placeholder
      qualityScore: successfulMessages > 0 ? 0.88 : 0 // Placeholder
    };
  }, [pendingMessages, agentResponses, availableAgents, orchestrationPlans]);

  return {
    // Enhanced core methods
    sendMessage,
    runMultiAgentTask,
    findOptimalAgents,
    getA2APerformanceMetrics,
    
    // Existing methods with enhanced capabilities
    getAgentByCapability,
    advertiseCapabilities,
    sendPeerMessage,
    findBestPeerAgent,
    
    // State management
    pendingMessages,
    peerMessages,
    agentResponses,
    isProcessing: isProcessing || isRouting,
    availableAgents,
    orchestrationPlans,
    orchestrationActive: isProcessing || isRouting,
    taskComplexity: 'medium',
    
    // Communication channels
    createDirectChannel,
    messageStats,
    getMessageBusStats,
    broadcastNotification: (content: string, type: 'info' | 'warning' | 'error' | 'success') => 
      broadcastNotification('enhanced-executive-agent', content, type),
    subscribeToMessages,
    
    // Context and operations
    ...contextOps,
    ...calendarOps,
    ...dataAgentOps,
    ...schedulingAgent
  };
};
